import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Typography } from "@mui/material";
import { ArrowForwardRounded, ArticleOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { NewsCategory, NewsItem } from "../../../Models/ambassador";
import { useNews } from "../../../Query/useAmbassador";
import { cardSx } from "../../HomePage/DashboardCard";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { formatDate } from "../Common/format";
import {
  NEWS_CATEGORIES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";
import { CATEGORY_COLORS } from "./NewsTeaserList";

const PAGE_SIZE = 9;

const NewsCard: React.FC<{ item: NewsItem }> = ({ item }) => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const { primary, accentText } = useDashboardColors();
  const category = labels.newsCategory(item.category);

  return (
    <Box
      component={RouterLink}
      to={ambassadorPaths.newsItem(item.id)}
      sx={{
        ...cardSx,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        textDecoration: "none",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(16, 24, 40, 0.08)" },
      }}
    >
      <Box
        aria-hidden
        sx={{
          height: 64,
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${CATEGORY_COLORS[item.category]}, ${primary})`,
          color: color.white,
          display: "flex",
          alignItems: "center",
          px: 2,
          fontWeight: 800,
          letterSpacing: "0.06em",
          fontSize: 14,
        }}
      >
        {category.toUpperCase()}
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: CATEGORY_COLORS[item.category],
        }}
      >
        {category.toUpperCase()} ·{" "}
        {formatDate(item.published_at, i18n.language)}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: primary }}>
        {item.title}
      </Typography>
      {item.teaser && <Typography variant="body2">{item.teaser}</Typography>}
      <Typography
        sx={{
          mt: "auto",
          pt: 1,
          fontSize: 13,
          fontWeight: 700,
          color: accentText,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {t("news.readMore")}
        <ArrowForwardRounded sx={{ fontSize: 16 }} />
      </Typography>
    </Box>
  );
};

const FeaturedNews: React.FC<{ item: NewsItem }> = ({ item }) => {
  const { t, i18n } = useTranslation("ambassador");
  const navigate = useNavigate();
  const labels = useAmbassadorLabels();
  const { primary } = useDashboardColors();

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 4 },
        borderRadius: "16px",
        background: `linear-gradient(120deg, ${primary}, ${CATEGORY_COLORS[item.category]})`,
        color: color.white,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "rgba(255,255,255,0.85)",
        }}
      >
        {t("news.latest").toUpperCase()} ·{" "}
        {labels.newsCategory(item.category).toUpperCase()} ·{" "}
        {formatDate(item.published_at, i18n.language)}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 22, md: 28 },
          fontWeight: 800,
          mt: 1,
          color: color.white,
          maxWidth: 720,
        }}
      >
        {item.title}
      </Typography>
      {item.teaser && (
        <Typography
          sx={{
            fontSize: 15,
            mt: 1,
            color: "rgba(255,255,255,0.85)",
            maxWidth: 720,
          }}
        >
          {item.teaser}
        </Typography>
      )}
      <Button
        variant="contained"
        endIcon={<ArrowForwardRounded />}
        onClick={() => navigate(ambassadorPaths.newsItem(item.id))}
        sx={{ mt: 2.5 }}
      >
        {t("news.read")}
      </Button>
    </Box>
  );
};

/** Published news for ambassadors, newest first, filterable by category. */
const NewsList: React.FC = () => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const [category, setCategory] = useState<NewsCategory | null>(null);
  const [pages, setPages] = useState(1);
  const news = useNews({ page: 1, page_size: PAGE_SIZE * pages, category });

  const items = news.data?.news ?? [];
  const [featured, ...others] = items;
  const hasMore = items.length < (news.data?.total ?? 0);

  const chip = (value: NewsCategory | null, label: string) => {
    const active = category === value;
    return (
      <Chip
        key={label}
        label={label}
        onClick={() => {
          setCategory(value);
          setPages(1);
        }}
        color={active ? "primary" : "default"}
        variant={active ? "filled" : "outlined"}
        sx={{
          fontWeight: 700,
          borderColor: color.gray200,
          backgroundColor: active ? undefined : color.white,
        }}
      />
    );
  };

  return (
    <>
      <PageHeader title={t("news.title")} subtitle={t("news.subtitle")} />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
        {chip(null, t("news.allCategories"))}
        {NEWS_CATEGORIES.map((value) =>
          chip(value, labels.newsCategory(value)),
        )}
      </Box>

      {news.isLoading ? (
        <LoadingState />
      ) : news.isError ? (
        <ErrorState onRetry={() => news.refetch()} />
      ) : !featured ? (
        <Box sx={cardSx}>
          <EmptyState
            icon={<ArticleOutlined />}
            title={t("news.emptyTitle")}
            text={t("news.emptyText")}
          />
        </Box>
      ) : (
        <>
          <FeaturedNews item={featured} />
          {others.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: color.gray900,
                  mb: 2,
                }}
              >
                {t("news.more")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    md: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {others.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </Box>
            </>
          )}
          {hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setPages(pages + 1)}
                disabled={news.isFetching}
              >
                {t("news.loadMore")}
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default NewsList;
